/**
 * Tiny inline SVG line chart. Avoids pulling in recharts/chart.js for our
 * one-or-two charts. Takes an array of {label, value} points.
 *
 * Renders an outlined line + filled area, with min/max labels and an x-axis
 * that shows the first and last bucket label (full label rendering would
 * be cluttered for long series).
 */

import React from 'react';

export default function Sparkline({ data = [], height = 220, valueFormat = (v) => String(v) }) {
  if (!data || data.length === 0) {
    return <div className="admin-chart-empty">No data for this range yet.</div>;
  }

  const w = 760;
  const h = height;
  const padL = 50, padR = 16, padT = 16, padB = 26;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const values = data.map((d) => Number(d.value) || 0);
  const minV = Math.min(...values, 0);
  const maxV = Math.max(...values, 1);
  const range = maxV - minV || 1;

  const stepX = data.length > 1 ? innerW / (data.length - 1) : innerW;
  const points = data.map((d, i) => {
    const x = padL + stepX * i;
    const y = padT + innerH - (((Number(d.value) || 0) - minV) / range) * innerH;
    return { x, y, label: d.label, value: d.value };
  });

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');
  const fillD =
    pathD +
    ` L${(padL + innerW).toFixed(1)},${(padT + innerH).toFixed(1)}` +
    ` L${padL.toFixed(1)},${(padT + innerH).toFixed(1)} Z`;

  return (
    <svg className="admin-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      {/* y-axis ticks */}
      <line x1={padL} y1={padT} x2={padL} y2={padT + innerH} stroke="#e6e8eb" />
      <text x={padL - 6} y={padT + 4} textAnchor="end" fontSize="10" fill="#8a929c">
        {valueFormat(maxV)}
      </text>
      <text x={padL - 6} y={padT + innerH} textAnchor="end" fontSize="10" fill="#8a929c">
        {valueFormat(minV)}
      </text>
      {/* x-axis */}
      <line
        x1={padL} y1={padT + innerH} x2={padL + innerW} y2={padT + innerH}
        stroke="#e6e8eb"
      />
      <text x={padL} y={h - 6} fontSize="10" fill="#8a929c">{data[0]?.label}</text>
      <text x={padL + innerW} y={h - 6} textAnchor="end" fontSize="10" fill="#8a929c">
        {data[data.length - 1]?.label}
      </text>
      {/* fill */}
      <path d={fillD} fill="rgba(26,82,118,0.08)" />
      {/* line */}
      <path d={pathD} fill="none" stroke="#1a5276" strokeWidth="2" />
      {/* dots — only render up to ~20 to keep it readable */}
      {points.length <= 20 &&
        points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="2.5" fill="#1a5276">
            <title>{`${p.label}: ${valueFormat(p.value)}`}</title>
          </circle>
        ))}
    </svg>
  );
}
